import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { competenciesApi } from '../competencyApi';
import type { Competency } from '../competencyTypes';
import { sampleCompetency } from '../utils/sampleCompetency';

/**
 * Create or edit one competency in the framework.
 *
 * No position field: where a competency sits in the framework is assigned by the server and
 * changed by moving the row, not by typing a number here — a rename is not a reorder.
 */
export function CompetencyModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: Competency | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [category, setCategory] = useState(editing?.category ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      category: category.trim() || null,
      description: description.trim() || null,
    };

    try {
      if (editing) await competenciesApi.update(editing.id, request);
      else await competenciesApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this competency.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'competency' });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleCompetency();

    setName(sample.name);
    setCategory(sample.category);
    setDescription(sample.description);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Names the row on an edit, so a reader who opened the wrong one sees it before they type.
      title={editing ? `Edit ${editing.name}` : 'New competency'}
      size="2xl"
      id="competency-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'competency-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="competency-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="competency-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="competency-name"
            label="Competency"
            required
            description="As the framework names it — the words an L&D record will be measured against."
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={name.trim() ? error?.fieldError('name') : "Enter the competency's name."}
            disabled={saving}
          />

          <TextField
            id="competency-category"
            label="Group"
            /*
              Free text rather than a picker, and the description says so: a framework's
              groupings are few and change with the framework itself, so a catalog of them would
              be a catalog of a catalog.
            */
            description="The grouping within the framework — Core, Leadership, Technical."
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            error={error?.fieldError('category')}
            disabled={saving}
          />
        </div>

        <TextField
          id="competency-description"
          label="Description"
          description="What the competency covers. Read here, on this page — not by the clerk filling in a 201 file."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={error?.fieldError('description')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
